import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  Blocks,
  Building2,
  ChevronDown,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Receipt,
  Settings,
  ShieldCheck,
  Sun,
  Tags,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button, Select, Spinner, cx, formatDateTime } from './ui';

/**
 * Estrutura da aplicacao: menu do §50, seletor de empresa e filial, e a
 * identidade do usuario.
 *
 * Os itens de menu sao filtrados por permissao — mostrar o que o usuario nao pode
 * abrir so produz um 403 depois do clique.
 */

interface NavItem {
  label: string;
  href: string;
  permission?: string;
}

interface NavSection {
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    label: 'Cadastros',
    icon: <Users size={16} />,
    items: [
      { label: 'Clientes e fornecedores', href: '/partners', permission: 'partners.customer.read' },
      { label: 'Grupos de clientes', href: '/customer-groups', permission: 'partners.group.read' },
      { label: 'Produtos e servicos', href: '/items', permission: 'catalog.item.read' },
      { label: 'Categorias', href: '/categories', permission: 'catalog.category.read' },
    ],
  },
  {
    label: 'Precos',
    icon: <Tags size={16} />,
    items: [
      { label: 'Listas de preco', href: '/price-lists', permission: 'pricing.price_list.read' },
      { label: 'Consultar preco', href: '/price-check', permission: 'pricing.price_list.read' },
    ],
  },
  {
    label: 'Faturamento',
    icon: <Blocks size={16} />,
    items: [
      { label: 'Central de Faturamento', href: '/billing', permission: 'billing.billing.read' },
      { label: 'Contratos', href: '/contracts', permission: 'contracts.contract.read' },
      { label: 'Itens de contrato', href: '/contract-items', permission: 'contracts.contract.read' },
      { label: 'Vendas avulsas', href: '/sales', permission: 'sales.sale.read' },
      { label: 'Indices economicos', href: '/economic-indexes', permission: 'contracts.adjustment.read' },
    ],
  },
  {
    label: 'Financeiro',
    icon: <Wallet size={16} />,
    items: [
      { label: 'Contas a receber', href: '/receivables', permission: 'finance.receivable.read' },
      { label: 'Contas a pagar', href: '/payables', permission: 'finance.payable.read' },
      { label: 'Contas bancarias', href: '/bank-accounts', permission: 'finance.bank_account.read' },
      { label: 'Fluxo de caixa', href: '/cash-flow', permission: 'finance.report.read' },
      { label: 'DRE gerencial', href: '/income-statement', permission: 'finance.report.read' },
    ],
  },
  {
    label: 'Estrutura financeira',
    icon: <Receipt size={16} />,
    items: [
      { label: 'Plano de contas', href: '/chart-of-accounts', permission: 'finance.chart_of_account.read' },
      { label: 'Categorias financeiras', href: '/financial-categories', permission: 'finance.category.read' },
      { label: 'Centros de custo', href: '/cost-centers', permission: 'finance.cost_center.read' },
      { label: 'Politica e feriados', href: '/financial-policies', permission: 'finance.policy.manage' },
    ],
  },
  {
    label: 'Fiscal e cobranca',
    icon: <FileText size={16} />,
    items: [
      { label: 'NFS-e emitidas', href: '/fiscal-documents', permission: 'fiscal.nfse.read' },
      { label: 'Cobrancas', href: '/charges', permission: 'collections.charge.read' },
      { label: 'Conciliacao bancaria', href: '/reconciliation', permission: 'finance.statement.read' },
      { label: 'Mensagens', href: '/messaging', permission: 'messaging.delivery.read' },
      { label: 'Portal do cliente', href: '/portal-admin', permission: 'portal.user.read' },
      // Fica em operacao, e nao em configuracoes: a caixa de pendentes e trabalho
      // diario de quem aprova. A edicao de regras mora na segunda aba, atras de
      // outra permissao.
      { label: 'Aprovacoes', href: '/approvals', permission: 'platform.approval.decide' },
      // Faturamento, receita recorrente e inadimplencia. Os relatorios que sao lista
      // saem pelo botao Exportar da propria tela.
      { label: 'Relatorios', href: '/reports', permission: 'finance.report.read' },
      // A tela do contador: quatro coisas por mes, num lugar. O perfil CONTADOR ve
      // praticamente so esta.
      { label: 'Fechamento fiscal', href: '/fiscal-closing', permission: 'fiscal.closing.read' },
    ],
  },
  {
    label: 'Configuracoes',
    icon: <Settings size={16} />,
    items: [
      { label: 'Empresas e filiais', href: '/companies', permission: 'companies.company.read' },
      { label: 'Usuarios', href: '/users', permission: 'auth.user.read' },
      { label: 'Perfis de acesso', href: '/roles', permission: 'auth.role.read' },
      { label: 'Campos personalizados', href: '/custom-fields', permission: 'platform.field_definition.read' },
      { label: 'Consultas externas', href: '/lookup-providers', permission: 'platform.lookup.query' },
      { label: 'Importar do sistema antigo', href: '/imports', permission: 'platform.import.manage' },
      { label: 'Emissao fiscal', href: '/certificates', permission: 'fiscal.certificate.read' },
      {
        label: 'Integracoes bancarias',
        href: '/bank-integrations',
        permission: 'collections.credential.read',
      },
      { label: 'Privacidade e LGPD', href: '/privacy', permission: 'platform.privacy.read' },
    ],
  },
];

/**
 * Fases seguintes, exibidas para dar contexto do produto.
 *
 * Vazia desde que a Fase 5 entrou: NFS-e, cobranca, conciliacao e envio saíram
 * da promessa e viraram menu. O portal do cliente (Fase 6) entra aqui quando
 * comecar — anunciar antes disso treina o usuario a ignorar a secao.
 */
const UPCOMING: Array<{ label: string; icon: React.ReactNode; phase: string }> = [];

export function AppShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { user, loading, logout, can, companyId, branchId, setScope } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark' | null>(null);

  useEffect(() => {
    if (!loading && !user) window.location.assign('/login');
  }, [loading, user]);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('dwu.theme');
      if (stored === 'light' || stored === 'dark') {
        setTheme(stored);
        document.documentElement.dataset.theme = stored;
      }
    } catch {
      /* preferencia apenas nesta sessao */
    }
  }, []);

  // Trocar de pagina fecha o menu no celular.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.dataset.theme = next;
    try {
      window.localStorage.setItem('dwu.theme', next);
    } catch {
      /* ignora */
    }
  };

  const sections = useMemo(
    () =>
      SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.permission || can(item.permission)),
      })).filter((section) => section.items.length > 0),
    [can],
  );

  const branches = useMemo(() => {
    if (!user || !companyId) return [];
    return user.companyAccess
      .filter((access) => access.company.id === companyId && access.branch)
      .map((access) => access.branch!);
  }, [user, companyId]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Carregando sessao..." />
      </div>
    );
  }

  if (!user) return null;

  const companies = user.companyAccess
    .map((access) => access.company)
    .filter((company, index, list) => list.findIndex((item) => item.id === company.id) === index);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 border-b border-rule bg-surface">
        <div className="flex h-14 items-center gap-3 px-4">
          <button
            onClick={() => setMenuOpen((current) => !current)}
            className="rounded-sm p-1.5 text-ink-2 transition hover:bg-surface-2 lg:hidden"
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>

          <Link to="/" className="flex items-baseline gap-1.5">
            <span className="font-mono text-sm font-medium tracking-[0.14em] text-accent">DWU</span>
            <span className="text-sm text-ink-3">ERP</span>
          </Link>

          <div className="ml-auto flex items-center gap-2">
            {companies.length > 0 ? (
              <Select
                value={companyId ?? ''}
                onChange={(event) => setScope(event.target.value, null)}
                className="h-8 w-auto max-w-[14rem] text-xs"
                aria-label="Empresa ativa"
              >
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.tradeName ?? company.legalName}
                  </option>
                ))}
              </Select>
            ) : null}

            {branches.length > 1 ? (
              <Select
                value={branchId ?? ''}
                onChange={(event) => setScope(companyId!, event.target.value || null)}
                className="h-8 w-auto max-w-[10rem] text-xs"
                aria-label="Filial ativa"
              >
                <option value="">Todas as filiais</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>
            ) : null}

            <NotificationBell />

            <button
              onClick={toggleTheme}
              className="rounded-sm p-1.5 text-ink-3 transition hover:bg-surface-2 hover:text-ink"
              aria-label="Alternar tema"
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <UserMenu name={user.fullName} email={user.email} admin={user.isTenantAdmin} onLogout={logout} />
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        <nav
          className={cx(
            'w-60 shrink-0 border-r border-rule bg-surface px-3 py-4',
            'lg:block',
            menuOpen ? 'fixed inset-y-14 left-0 z-20 block overflow-y-auto' : 'hidden',
          )}
        >
          <Link to="/"
            className={cx(
              'mb-3 flex items-center gap-2 rounded-md px-2.5 py-2 text-sm transition',
              pathname === '/'
                ? 'bg-accent-soft font-medium text-accent'
                : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
            )}
          >
            <LayoutDashboard size={16} />
            Painel
          </Link>

          {sections.map((section) => (
            <div key={section.label} className="mb-4">
              <p className="mb-1 flex items-center gap-2 px-2.5 text-2xs font-medium uppercase tracking-wide text-ink-3">
                {section.icon}
                {section.label}
              </p>
              {section.items.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cx(
                    'block rounded-md px-2.5 py-1.5 text-sm transition',
                    pathname === item.href || pathname.startsWith(`${item.href}/`)
                      ? 'bg-accent-soft font-medium text-accent'
                      : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          ))}

          <div className="mt-6 border-t border-rule pt-3">
            <p className="mb-1 px-2.5 text-2xs font-medium uppercase tracking-wide text-ink-3">
              Em construcao
            </p>
            {UPCOMING.map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-sm text-ink-3"
                title={`Previsto para a ${item.phase}`}
              >
                <span className="flex items-center gap-2 opacity-60">
                  {item.icon}
                  {item.label}
                </span>
                <span className="shrink-0 rounded-sm bg-surface-2 px-1 py-0.5 text-2xs">{item.phase}</span>
              </div>
            ))}
          </div>
        </nav>

        <main className="min-w-0 flex-1 px-4 py-6 lg:px-8">
          <div className="mx-auto max-w-[110rem]">{children}</div>
        </main>
      </div>
    </div>
  );
}

function UserMenu({
  name,
  email,
  admin,
  onLogout,
}: {
  name: string;
  email: string;
  admin: boolean;
  onLogout: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);

  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((current) => !current)}
        className="flex items-center gap-1.5 rounded-md px-1.5 py-1 transition hover:bg-surface-2"
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-accent-soft text-2xs font-semibold text-accent">
          {initials}
        </span>
        <ChevronDown size={14} className="text-ink-3" />
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-20 mt-1 w-60 rounded-md border border-rule bg-surface p-1 shadow-pop">
            <div className="px-2.5 py-2">
              <p className="truncate text-sm font-medium text-ink">{name}</p>
              <p className="truncate text-xs text-ink-3">{email}</p>
              {admin ? (
                <span className="mt-1.5 inline-flex items-center gap-1 rounded-sm bg-accent-soft px-1.5 py-0.5 text-2xs font-medium text-accent">
                  <ShieldCheck size={11} />
                  Administrador
                </span>
              ) : null}
            </div>
            <div className="my-1 border-t border-rule" />
            <Button
              variant="ghost"
              size="sm"
              icon={<LogOut size={14} />}
              onClick={() => void onLogout()}
              className="w-full justify-start"
            >
              Sair
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Aviso de permissao insuficiente, para telas que o menu nao filtrou. */
export function PermissionGate({
  allowed,
  children,
}: {
  allowed: boolean;
  children: React.ReactNode;
}) {
  if (allowed) return <>{children}</>;

  return (
    <div className="rounded-lg border border-rule bg-surface p-8 text-center shadow-card">
      <Building2 size={20} className="mx-auto mb-2 text-ink-3" />
      <p className="text-sm font-medium text-ink">Sem permissao para esta tela</p>
      <p className="mt-1 text-sm text-ink-3">
        Procure o administrador do sistema para solicitar acesso.
      </p>
    </div>
  );
}
// --- central de notificacoes (§7) -------------------------------------------

interface NotificationRow {
  id: string;
  kind: string;
  severity: 'INFO' | 'AVISO' | 'CRITICO';
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

/**
 * Sino da central de notificações (§7).
 *
 * Vive no `AppShell` porque o aviso tem de alcançar quem está em qualquer tela —
 * pendurá-lo numa página faria o alerta depender de a pessoa estar justamente ali.
 *
 * O contador é consultado a cada minuto e **só o contador**: puxar a lista inteira
 * nesse intervalo gastaria banda e banco para mostrar um número. A lista vem quando
 * a pessoa abre.
 */
function NotificationBell() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);

  const loadCount = useCallback(async () => {
    try {
      const result = await api.get<{ unread: number }>('/notifications/unread-count');
      setUnread(result.unread);
    } catch {
      // Silencioso de propósito: falha ao contar avisos não pode pôr erro na casca
      // de toda a aplicação. O número fica velho, e a próxima tentativa corrige.
    }
  }, []);

  useEffect(() => {
    void loadCount();
    const timer = window.setInterval(() => void loadCount(), 60_000);
    return () => window.clearInterval(timer);
  }, [loadCount]);

  const openBox = async () => {
    setOpen(true);
    setLoading(true);

    try {
      const result = await api.get<{ items: NotificationRow[]; unread: number }>('/notifications');
      setItems(result.items);
      setUnread(result.unread);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const go = async (item: NotificationRow) => {
    setOpen(false);

    if (item.readAt === null) {
      // Marca ao abrir, não ao fechar a caixa: abrir é o ato de tomar conhecimento, e
      // marcar tudo ao fechar apagaria o que a pessoa deixou para depois.
      try {
        await api.post('/notifications/read', { ids: [item.id] });
        setUnread((current) => Math.max(0, current - 1));
      } catch {
        /* o aviso continua na caixa, que é o lado seguro */
      }
    }

    if (item.link) navigate(item.link);
  };

  const markAll = async () => {
    try {
      await api.post('/notifications/read-all');
      setUnread(0);
      setItems((current) => current.map((item) => ({ ...item, readAt: new Date().toISOString() })));
    } catch {
      /* nada muda na tela se falhou */
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => (open ? setOpen(false) : void openBox())}
        className="relative rounded-sm p-1.5 text-ink-3 transition hover:bg-surface-2 hover:text-ink"
        aria-label={unread > 0 ? `Avisos (${unread} nao lidos)` : 'Avisos'}
      >
        <Bell size={16} />
        {unread > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-critical px-1 text-[10px] font-medium text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 z-40 mt-1 w-80 rounded-md border border-rule bg-surface shadow-pop">
            <div className="flex items-center justify-between border-b border-rule px-3 py-2">
              <p className="text-xs font-medium uppercase tracking-wide text-ink-3">Avisos</p>
              <div className="flex items-center gap-2">
                {unread > 0 ? (
                  <button
                    onClick={() => void markAll()}
                    className="text-xs text-accent hover:underline"
                  >
                    Marcar todos
                  </button>
                ) : null}
                <Link
                  to="/notifications"
                  onClick={() => setOpen(false)}
                  className="text-xs text-ink-3 hover:text-ink"
                >
                  Preferencias
                </Link>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <p className="px-3 py-4 text-sm text-ink-3">Carregando...</p>
              ) : items.length === 0 ? (
                <p className="px-3 py-4 text-sm text-ink-3">Nada por aqui.</p>
              ) : (
                items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => void go(item)}
                    className={cx(
                      'flex w-full flex-col gap-0.5 border-b border-rule/50 px-3 py-2 text-left transition hover:bg-surface-2',
                      item.readAt === null ? 'bg-accent-soft/30' : '',
                    )}
                  >
                    <span className="flex items-start gap-1.5">
                      {item.severity === 'CRITICO' ? (
                        <AlertTriangle size={13} className="mt-0.5 shrink-0 text-critical" />
                      ) : null}
                      <span className="text-sm text-ink">{item.title}</span>
                    </span>
                    {item.body ? (
                      <span className="line-clamp-2 text-xs text-ink-2">{item.body}</span>
                    ) : null}
                    <span className="text-2xs text-ink-3">{formatDateTime(item.createdAt)}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
